// router.js — History API + a 30-line matcher.
//
// Routes are simple path patterns with :param capture and an optional ":rest*"
// catch-all. The matched component is rendered into the host element passed
// to start(). Navigation goes through navigate() so we can intercept link
// clicks and back/forward without page reloads.

import { t } from "./core/i18n.js";

/**
 * @typedef {object} Route
 * @property {string} path                   e.g. "/lesson/:itemId"
 * @property {string} tag                    custom element tag to mount
 * @property {(params: Record<string,string>) => Record<string,string>} [props]
 */

/** @type {Route[]} */ let routes = [];
/** @type {HTMLElement | null} */ let host = null;

/** @param {Route[]} rs @param {HTMLElement} h */
export function start(rs, h) {
  routes = rs;
  host = h;
  window.addEventListener("popstate", render);
  document.addEventListener("click", onLinkClick);
  render();
}

/** @param {string} path */
export function navigate(path) {
  if (path === location.pathname) return;
  history.pushState({}, "", path);
  document.dispatchEvent(new CustomEvent("kfe:route-changed", { detail: { path } }));
  render();
}

/**
 * Re-attach the router to a new host element and re-render the current
 * route into it. Used by the shell after it re-renders itself (e.g. when
 * i18n strings arrive) and the previous <main> outlet has been replaced.
 * @param {HTMLElement} h
 */
export function remount(h) {
  host = h;
  render();
}

function render() {
  if (!host) return;
  const path = location.pathname || "/";
  for (const r of routes) {
    const params = match(r.path, path);
    if (params) {
      const el = document.createElement(r.tag);
      const props = r.props ? r.props(params) : params;
      for (const [k, v] of Object.entries(props)) /** @type {any} */ (el)[k] = v;
      host.replaceChildren(el);
      return;
    }
  }
  // No route matched. Distinguish two cases:
  //   • routes not registered yet (boot still in progress — the shell calls
  //     start() on kfe:ready, but an earlier kfe:i18n re-mount can render()
  //     against an empty route table first). This is NOT a 404 — show a
  //     neutral loading line, never the "Not found" error (Brief 122,
  //     Symptom A). The i18n bundle is loaded before any render, so t() is safe.
  //   • routes registered but the path genuinely matches nothing → real 404.
  if (routes.length === 0) {
    host.replaceChildren(Object.assign(document.createElement("p"), {
      className: "route-loading",
      textContent: t("loading"),
    }));
    return;
  }
  host.replaceChildren(Object.assign(document.createElement("p"), { textContent: "Not found: " + path }));
}

/**
 * @param {string} pattern
 * @param {string} path
 * @returns {Record<string,string> | null}
 */
function match(pattern, path) {
  const pp = pattern.replace(/\/$/, "").split("/");
  const ap = path.replace(/\/$/, "").split("/");
  /** @type {Record<string,string>} */
  const params = {};
  for (let i = 0; i < pp.length; i++) {
    const p = pp[i];
    const a = ap[i];
    if (p?.startsWith(":")) {
      if (p.endsWith("*")) {
        params[p.slice(1, -1)] = ap.slice(i).join("/");
        return params;
      }
      if (a == null) return null;
      params[p.slice(1)] = decodeURIComponent(a);
    } else if (p !== a) {
      return null;
    }
  }
  return ap.length === pp.length ? params : null;
}

/** @param {MouseEvent} e */
function onLinkClick(e) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  // Click events from inside a shadow root are retargeted on the way to
  // document, so e.target is the shadow host (e.g. <kfe-pathway>), not the
  // <a>. composedPath() walks the original event path and lets us find the
  // real <a> the user clicked.
  const path = e.composedPath ? e.composedPath() : [];
  /** @type {HTMLAnchorElement | null} */
  let a = null;
  for (const n of path) {
    if (n instanceof HTMLAnchorElement) { a = n; break; }
  }
  if (!a) return;
  if (a.target && a.target !== "_self") return;
  // A download anchor is a file save, not in-app navigation. Brief 021's
  // desktop fallback (`_downloadCard`) clicks a temporary `<a download>` on a
  // blob: URL; a blob URL's origin equals the page origin, so without this
  // guard the router would treat it as a same-origin link, preventDefault the
  // download, and navigate to the blob's UUID pathname → "Not found: /<uuid>".
  if (a.hasAttribute("download")) return;
  // Non-http(s) schemes (blob:, data:, mailto:, tel:) are never SPA routes.
  if (!/^https?:$/.test(new URL(a.href).protocol)) return;
  const url = new URL(a.href);
  if (url.origin !== location.origin) return;
  e.preventDefault();
  navigate(url.pathname);
}
